import { useEffect, useState, useCallback } from "react";
import { VERSION_CHECK_INTERVAL } from "src/consts/interval";

const CURRENT_VERSION = process.env.BIG_FLIP_GIT_SHA || "dev";
const VERSION_CHECK_KEY = "bigflip_last_version_check";

interface VersionCheckResult {
  hasNewVersion: boolean;
  currentVersion: string;
  newVersion: string | null;
  checkVersion: () => Promise<void>;
  reloadApp: () => void;
}

/**
 * Custom hook to check if a new version of the app is available
 *
 * This hook periodically checks the meta tag in the deployed index.html
 * to see if the version has changed, indicating a new deployment.
 */
export function useVersionCheck(): VersionCheckResult {
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [newVersion, setNewVersion] = useState<string | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      // Don't check in development
      if (process.env.NODE_ENV === "development") {
        return;
      }

      // Fetch the index.html with cache-busting
      const timestamp = new Date().getTime();

      const response = await fetch(`/index.html?t=${timestamp}`, {
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache"
        }
      });

      if (!response.ok) {
        console.warn("[VersionCheck] Failed - status:", response.status);
        return;
      }

      const html = await response.text();

      // Extract version from meta tag
      const versionMatch = html.match(/<meta name="ui-version" content="([^"]+)"/);

      if (versionMatch && versionMatch[1]) {
        const deployedVersion = versionMatch[1];

        // Update last check timestamp
        localStorage.setItem(VERSION_CHECK_KEY, Date.now().toString());

        // Check if versions differ
        if (deployedVersion !== CURRENT_VERSION) {
          setHasNewVersion(true);
          setNewVersion(deployedVersion);
        } else {
          // Reset if we're back on the same version
          setHasNewVersion(false);
          setNewVersion(null);
        }
      }
    } catch (error) {
      console.error("[VersionCheck] ❌ Error:", error);
      // Silently fail - don't disrupt user experience
    }
  }, []);

  const reloadApp = useCallback(() => {
    // Clear localStorage related to version checking
    localStorage.removeItem(VERSION_CHECK_KEY);

    // Hard reload to get new version
    window.location.reload();
  }, []);

  useEffect(() => {
    // Initial check on mount
    checkVersion();

    // Set up periodic checking
    const intervalId = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    // Also check when window regains focus (user comes back to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const lastCheck = localStorage.getItem(VERSION_CHECK_KEY);
        const timeSinceLastCheck = lastCheck ? Date.now() - parseInt(lastCheck, 10) : Infinity;

        // Only check if it's been more than 1 minute since last check
        if (timeSinceLastCheck > 60000) {
          checkVersion();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkVersion]);

  return {
    hasNewVersion,
    currentVersion: CURRENT_VERSION,
    newVersion,
    checkVersion,
    reloadApp
  };
}
